import React, { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import { fetchById, simpleFetch } from "../../utils/helpsers";
import Grid from '@mui/material/Grid';
import DataGridTable from "../../components/organisms/Table/DataGridTable";
import LayoutPage from "../../templates/Layout/LayoutPage";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import ReportButtons from "../../components/organisms/BugReport/ReportButtons";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Container from "@mui/material/Container";

require('highcharts/modules/exporting')(Highcharts);
require('highcharts/modules/export-data')(Highcharts);

HighchartsMore(Highcharts);

/**
 *
 * @param {object} data
 *  The columns/rows fetched by district.
 * @returns {Object}
 *  The same object structure but updated date values.
 */
const normalizeData = (data) => {
  data[0].columns = (data[0].columns).map(item => {
    if (item.field === "election_date") {
      return ({ ...item, type: 'date' })
    } else {
      return item
    }
  });

  data[0].rows = (data[0].rows).map(item => ({ ...item, election_date: new Date(item.election_date.replaceAll("-", "/")) }));

  return data[0]
}

function ElectionHistory() {
  // Data to feed select fields.
  const [districtOptions, setDistrictOptions] = useState([]);

  // Object value/label from select component
  const [districtInfo, setDistrictInfo] = useState();
  const [defaultValue, setDefaultValue] = useState()

  const [levyRef, setLevyRef] = useState();
  const [bondRef, setBondRef] = useState();
  const [incomeTaxRef, setIncomeTaxRef] = useState();

  // User session metadata and data
  const { user } = useAuth0();
  const metadata = user[`${process.env.REACT_APP_AUTH0_KEY}/district`];

  const navigate = useNavigate();

  useEffect(() => {
    if (metadata) {
      const userSessionType = metadata.hasOwnProperty('sessionType') ? metadata.sessionType : 'forbidden';

      switch (userSessionType) {
        case 'district-access':
          const districtData = {
            value: parseInt(metadata.districtIrn),
            label: metadata.districtLabel
          };
          setDistrictInfo(districtData);
          break;
        case 'full-access':
          window.localStorage.setItem('session-type', 'full-access');
          break;
        case 'multiple-district-access':
          window.localStorage.setItem('session-type', 'multiple-district-access');
          break;
        case 'forbidden':
          navigate("/AccessDenied", { replace: true });
          break;
        default:
          navigate("/AccessDenied", { replace: true });
          break;
      }
    } else {
      navigate("/AccessDenied", { replace: true });
    }
  }, [navigate, user, metadata]);

  useEffect(() => {
    setTimeout(() => {
      // Get districts options.
      simpleFetch(`${process.env.REACT_APP_API_URL}/hvd_district?order=label`)
        .then(data => setDistrictOptions(data));

      // Get comparison group types.
      //setGroupTypesOptions(query.get('shared') ? [{ label: 'Custom Boards', value: 'custom_boards', selected: true }] : groupTypes);
    }, 1000);
  }, [user]);

  useEffect(() => {
    if (districtInfo) {
      let ref = districtInfo.value;
      let urlLevyRef = `${process.env.REACT_APP_API_URL}/rpc/levy_ref?_irn='`;
      let urlBondRef = `${process.env.REACT_APP_API_URL}/rpc/bond_ref?_irn='`;
      let urlIncomeTaxRef = `${process.env.REACT_APP_API_URL}/rpc/income_tax_ref?_irn='`;

      setLevyRef();
      fetchById(urlLevyRef, ref + "'")
        .then(data => {
          if (data[0].rows && data[0].columns) {
            setLevyRef(normalizeData(data));
          }
        });

      setBondRef();
      fetchById(urlBondRef, ref + "'")
        .then(data => {
          if (data[0].rows && data[0].columns) {
            setBondRef(normalizeData(data));
          }
        });

      setIncomeTaxRef();
      fetchById(urlIncomeTaxRef, ref + "'")
        .then(data => {
          if (data[0].rows && data[0].columns) {
            setIncomeTaxRef(normalizeData(data));
          }
        });
    }

    if (!districtInfo) {
      setDistrictInfo(districtOptions[0]);
      setDefaultValue(districtOptions[0]);
    }
  }, [districtInfo, districtOptions]);

  const topContentElement = (
    <Container maxWidth={"xl"} fixed className="section section--data-filters">
      <div >
        <Autocomplete
          id="districts"
          className="form-select"
          isOptionEqualToValue={(option, value) =>
            option === value
          }
          options={districtOptions}
          noOptionsText={'Loading...'}
          value={defaultValue || null}
          fullWidth={false}
          onChange={(event, newValue) => {
            // Matomo code.
            if (newValue) {
              setDistrictInfo(newValue);
              setDefaultValue(newValue);
              window._paq.push([
                "trackEvent",
                "Board ElectionHistory",
                "Clicked: Reference district",
                `Selected: ${newValue.label}`,
              ]);
            
            }
          }}
          sx={{ width: "300px" }}
          renderInput={(params) => (
            <TextField {...params} label="Reference district" />
          )}
        />
      </div>
    </Container>
  );

  const editorsNote = (
    <p>
      <strong>Editor's notes:</strong> (*)This result appears to be incorrectly reported in the source authority's reported data.
      Crocus has appended the result to reflect the For and Against tabulation found in the source reported table.
      Please contact{' '}
      <a href="mailto:help@insightboard.org">help@insightboard.org</a> for more information.
    </p>
  );

  return (
    <LayoutPage
      pageTitle="Election History"
      description="Build custom groups based on filters"
      type="dashboard"
      customClass="levy"
      topContent={topContentElement}
    >
      <div className="levy__cards">
        <Grid
          container
          spacing={2}
          direction="row"
          justifyContent="space-evenly"
          alignItems="center"
        >
          {levyRef && (
            <>
              <Grid item xs={12} className="table-data levy-table">
                <h2>Levy</h2>
                <DataGridTable
                  columns={levyRef.columns.map((item) => ({
                    ...item,
                    minWidth: 150,
                  }))}
                  rows={levyRef.rows}
                  sortField={"election_date"}
                  sortOrder={"desc"}
                  customStyles={{
                    '@media print': {
                      '& .MuiDataGrid-columnHeadersInner div[role="row"]': {
                        width: "100% !important",
                      },
                      '& .MuiDataGrid-columnHeadersInner div[role="row"] .MuiDataGrid-columnHeader': {
                        minWidth: "inherit !important",
                        flex: "1",
                      },
                      '& .MuiDataGrid-columnHeadersInner div[role="row"] .MuiDataGrid-columnHeaderTitle': {
                        //whiteSpace: "break-spaces !important",
                        textOverflow: "inherit !important",
                        lineHeight: "auto",
                        //whiteSpace: "normal",
                      },
                      '& .MuiDataGrid-columnHeadersInner div[role="row"] .MuiDataGrid-columnHeaderTitleContainerContent': {
                        width: "100% !important",
                      },
                      '& .MuiDataGrid-virtualScrollerRenderZone': {
                        width: "100% !important",
                      },
                      '& .MuiDataGrid-row': {
                        width: "100% !important",
                      },
                      '& .MuiDataGrid-row .MuiDataGrid-cell': {
                        minWidth: "inherit !important",
                        flex: "1",
                        whiteSpace: "break-spaces !important",
                        overflow: "inherit !important",
                        textOverflow: "inherit !important",
                      },
                    },
                  }}
                />
              </Grid>
              {
                (levyRef.rows).some( el => el.fixed_status) && (
                  <Grid item xs={12}>{editorsNote}</Grid>
                )
              }
            </>
          )}
          {bondRef && (
            <>
              <Grid item xs={12} className="table-data bond-table">
                <h2>Bond</h2>
                <DataGridTable
                  columns={bondRef.columns.map((item) => ({
                    ...item,
                    minWidth: 150,
                  }))}
                  rows={bondRef.rows}
                  sortField={"election_date"}
                  sortOrder={"desc"}
                  customStyles={{
                    '@media print': {
                      '& .MuiDataGrid-columnHeadersInner div[role="row"]': {
                        width: "100% !important",
                      },
                      '& .MuiDataGrid-columnHeadersInner div[role="row"] .MuiDataGrid-columnHeader': {
                        minWidth: "inherit !important",
                        flex: "1",
                      },
                      '& .MuiDataGrid-columnHeadersInner div[role="row"] .MuiDataGrid-columnHeaderTitleContainerContent': {
                        width: "100% !important",
                      },
                      '& .MuiDataGrid-virtualScrollerRenderZone': {
                        width: "100% !important",
                      },
                      '& .MuiDataGrid-row': {
                        width: "100% !important",
                      },
                      '& .MuiDataGrid-row .MuiDataGrid-cell': {
                        minWidth: "inherit !important",
                        flex: "1",
                        whiteSpace: "break-spaces !important",
                      },
                    },
                  }}
                />
              </Grid>
              {
                (bondRef.rows).some( el => el.fixed_status) && (
                  <Grid item xs={12}>{editorsNote}</Grid>
                )
              }
            </>
          )}
          {incomeTaxRef && (
            <>
              <Grid item xs={12} className="table-data incomeTax-table">
                <h2>Income Tax</h2>
                <DataGridTable
                  columns={incomeTaxRef.columns.map((item) => ({
                    ...item,
                    minWidth: 150,
                  }))}
                  rows={incomeTaxRef.rows}
                  sortField={"election_date"}
                  sortOrder={"desc"}
                  customStyles={{
                    '@media print': {
                      '& .MuiDataGrid-columnHeadersInner div[role="row"]': {
                        width: "100% !important",
                      },
                      '& .MuiDataGrid-columnHeadersInner div[role="row"] .MuiDataGrid-columnHeader': {
                        minWidth: "inherit !important",
                        flex: "1",
                      },
                      '& .MuiDataGrid-columnHeadersInner div[role="row"] .MuiDataGrid-columnHeaderTitleContainerContent': {
                        width: "100% !important",
                      },
                      '& .MuiDataGrid-virtualScrollerRenderZone': {
                        width: "100% !important",
                      },
                      '& .MuiDataGrid-row': {
                        width: "100% !important",
                      },
                      '& .MuiDataGrid-row .MuiDataGrid-cell': {
                        minWidth: "inherit !important",
                        flex: "1",
                        whiteSpace: "break-spaces !important",
                      },
                    },
                  }}
                />
              </Grid>
              {
                (incomeTaxRef.rows).some( el => el.fixed_status) && (
                  <Grid item xs={12}>{editorsNote}</Grid>                )
              }
            </>
          )}
        </Grid>
      </div>
      <ReportButtons
        boardType={"Election History"}
        collectedData={{
          districtInfo: districtInfo,
        }}
      />
    </LayoutPage>
  );
}

export default ElectionHistory;
